import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const OKRContext = createContext();

export const useOKR = () => {
  const context = useContext(OKRContext);
  if (!context) {
    throw new Error('useOKR must be used within an OKRProvider');
  }
  return context;
};

export const OKRProvider = ({ children }) => {
  const { user } = useAuth();
  const [cycles, setCycles] = useState([]);
  const [objectives, setObjectives] = useState([]);
  const [keyResults, setKeyResults] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const apiUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

  // Get authorization headers
  const getHeaders = () => ({
    headers: {
      Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
      'Content-Type': 'application/json',
    },
  });

  // Error handler
  const handleError = (error, context = '') => {
    console.error(`OKR ${context} Error:`, error);
    const message = error.response?.data?.detail || error.message || 'An error occurred';
    setError(message);
    throw new Error(message);
  };

  // Clear error
  const clearError = () => setError(null);

  // =============================================================================
  // OKR CYCLES
  // =============================================================================

  const fetchCycles = async (status = null) => {
    try {
      setLoading(true);
      clearError();
      
      const params = status ? { status } : {};
      const response = await axios.get(`${apiUrl}/api/okr-cycles`, {
        ...getHeaders(),
        params
      });
      
      setCycles(response.data);
      return response.data;
    } catch (error) {
      handleError(error, 'fetch cycles');
    } finally {
      setLoading(false);
    }
  };

  const createCycle = async (cycleData) => {
    try {
      setLoading(true);
      clearError();
      
      const response = await axios.post(`${apiUrl}/api/okr-cycles`, cycleData, getHeaders());
      
      setCycles(prev => [response.data, ...prev]);
      return response.data;
    } catch (error) {
      handleError(error, 'create cycle');
    } finally {
      setLoading(false);
    }
  };

  const updateCycleStatus = async (cycleId, newStatus) => {
    try {
      setLoading(true);
      clearError();
      
      await axios.put(`${apiUrl}/api/okr-cycles/${cycleId}/status`, newStatus, getHeaders());
      
      // Update local state
      setCycles(prev => prev.map(cycle => 
        cycle.id === cycleId ? { ...cycle, status: newStatus } : cycle
      ));
      
      return true;
    } catch (error) {
      handleError(error, 'update cycle status');
    } finally {
      setLoading(false);
    }
  };

  // =============================================================================
  // OBJECTIVES
  // =============================================================================

  const fetchObjectives = async (cycleId = null, level = null, assignedToMe = false) => {
    try {
      setLoading(true);
      clearError();
      
      const params = {};
      if (cycleId) params.cycle_id = cycleId;
      if (level) params.level = level;
      if (assignedToMe) params.assigned_to_me = true;
      
      const response = await axios.get(`${apiUrl}/api/objectives`, {
        ...getHeaders(),
        params
      });
      
      setObjectives(response.data);
      return response.data;
    } catch (error) {
      handleError(error, 'fetch objectives');
    } finally {
      setLoading(false);
    }
  };

  const createObjective = async (objectiveData) => {
    try {
      setLoading(true);
      clearError();
      
      const response = await axios.post(`${apiUrl}/api/objectives`, objectiveData, getHeaders());
      
      setObjectives(prev => [response.data, ...prev]);
      return response.data;
    } catch (error) {
      handleError(error, 'create objective');
    } finally {
      setLoading(false);
    }
  };

  const getObjective = async (objectiveId) => {
    try {
      setLoading(true);
      clearError();
      
      const response = await axios.get(`${apiUrl}/api/objectives/${objectiveId}`, getHeaders());
      return response.data;
    } catch (error) {
      handleError(error, 'get objective');
    } finally {
      setLoading(false);
    }
  };

  // =============================================================================
  // KEY RESULTS
  // =============================================================================

  const createKeyResult = async (keyResultData) => {
    try {
      setLoading(true);
      clearError();
      
      const response = await axios.post(`${apiUrl}/api/key-results`, keyResultData, getHeaders());
      
      // Update the parent objective's key results
      setObjectives(prev => prev.map(obj => 
        obj.id === keyResultData.objective_id 
          ? { ...obj, key_results: [...obj.key_results, response.data] }
          : obj
      ));
      
      return response.data;
    } catch (error) {
      handleError(error, 'create key result');
    } finally {
      setLoading(false);
    }
  };

  const updateKeyResult = async (keyResultId, updateData) => {
    try {
      setLoading(true);
      clearError();
      
      const response = await axios.put(`${apiUrl}/api/key-results/${keyResultId}`, updateData, getHeaders());
      
      // Update the key result in objectives
      setObjectives(prev => prev.map(obj => ({
        ...obj,
        key_results: obj.key_results.map(kr => 
          kr.id === keyResultId ? response.data : kr
        )
      })));
      
      return response.data;
    } catch (error) {
      handleError(error, 'update key result');
    } finally {
      setLoading(false);
    }
  };

  // =============================================================================
  // ANALYTICS
  // =============================================================================

  const fetchAnalytics = async (cycleId = null) => {
    try {
      setLoading(true);
      clearError();
      
      const params = cycleId ? { cycle_id: cycleId } : {};
      const response = await axios.get(`${apiUrl}/api/okr/analytics/summary`, {
        ...getHeaders(),
        params
      });
      
      setAnalytics(response.data);
      return response.data;
    } catch (error) {
      handleError(error, 'fetch analytics');
    } finally {
      setLoading(false);
    }
  };

  // =============================================================================
  // UTILITY FUNCTIONS
  // =============================================================================

  const getActiveCycle = () => {
    return cycles.find(cycle => cycle.status === 'active');
  };

  const getObjectivesByLevel = (level) => {
    return objectives.filter(obj => obj.level === level);
  };

  const calculateOKRProgress = (objective) => {
    if (!objective.key_results || objective.key_results.length === 0) {
      return 0;
    }
    
    const totalScore = objective.key_results.reduce((sum, kr) => sum + kr.score, 0);
    return (totalScore / objective.key_results.length) * 100;
  };

  // Initialize data when user changes
  useEffect(() => {
    if (user) {
      fetchCycles();
      fetchObjectives();
      fetchAnalytics();
    }
  }, [user]);

  const value = {
    // State
    cycles,
    objectives,
    keyResults,
    analytics,
    loading,
    error,
    
    // Cycle methods
    fetchCycles,
    createCycle,
    updateCycleStatus,
    
    // Objective methods
    fetchObjectives,
    createObjective,
    getObjective,
    
    // Key Result methods
    createKeyResult,
    updateKeyResult,
    
    // Analytics
    fetchAnalytics,
    
    // Utility methods
    clearError,
    getActiveCycle,
    getObjectivesByLevel,
    calculateOKRProgress,
  };

  return (
    <OKRContext.Provider value={value}>
      {children}
    </OKRContext.Provider>
  );
};